import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { Building2, ChevronRight, Mic, Play, Save, Square, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import * as z from 'zod';
import type { DamageType } from '@/common/damage';
import { DamageDimension } from '@/common/dropdowns';
import { DAMAGE_CLASSES, UNIT_TYPES } from '@/common/options';
import { ROLE } from '@/common/role';
import type { SubAsset } from '@/common/sub-asset';
import { DeleteButton } from '@/components/DeleteButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useAppContext } from '@/context/AppContext';
import { DROPDOWN_NAMES, useDamageTypeOptions, useMaterialOptions } from '@/hooks/use-dropdowns';
import { usePermission } from '@/hooks/use-permission';
import { useRevalidateOnLanguageChange } from '@/i18n/use-revalidate-on-language-change';
import { cn } from '@/lib/utils';
import { buildSubAssetPath, getTreeView } from '@/lib/utils/asset';
import { getAssetOptions } from '@/queries/assets/get-asset';
import { getListSubAssetsOptions } from '@/queries/sub-assets/list-sub-assets';

import { ConfigSelect } from '../ConfigSelect';
import { Restricted } from '../Restricted';
import { SubAssetItem } from '../sub-asset/sub-asset-list/SubAssetItem';

// --- HELPERS ---
// Helper: Reverse lookup to find ID by Unit Label (e.g., 'cm' -> '1')
const getUnitKeyByLabel = (label: string | undefined) => {
	if (!label) return '1';
	const entry = Object.entries(UNIT_TYPES).find(([_, val]) => val === label);
	return entry ? String(entry[0]) : '1';
};

// --- SCHEMA ---
const buildDamageSchema = (t: TFunction) =>
	z.object({
		assetId: z.string().optional(),
		subAssetId: z.string(),

		class: z.string().min(1, t('damages.validation.classRequired')),

		length: z.coerce.number().optional(),
		lengthUnit: z.string().optional().default('1'), //'cm'
		description: z.string().nullable().optional().or(z.literal('')),

		materialId: z.number({ error: t('damages.validation.materialRequired') }),
		damageTypeId: z.number().nullable(),
		statusId: z.number().nullable(),
		categoryId: z.number().nullable(),
		subCategoryId: z.number().nullable(),
		extentId: z.number().nullable(),
		longitudinalId: z.number().nullable(),
		transverseId: z.number().nullable(),
		elevationId: z.number().nullable(),
		previousDamageStatusId: z.number().nullable(),
		remarkId: z.number().nullable(),
	});

type DamageFormValues = z.input<ReturnType<typeof buildDamageSchema>>;

type DamageFormProps = {
	damage: DamageType | null;
	onSubmit: (data: any, shouldClose: boolean) => void;
	onDelete: () => void;
	onCancel: () => void;
	isSubmitting: boolean;
	calculateDamageSize: (damageId: string, measurementType: 'area' | 'perimeter') => any;
	/** Restore of a deleted item previewed read-only (GH #331), where Delete sits on a live one */
	restoreButton?: React.ReactNode;
};

export function DamageForm({
	damage,
	onSubmit,
	onDelete,
	onCancel,
	isSubmitting,
	calculateDamageSize,
	restoreButton,
}: DamageFormProps) {
	const { t } = useTranslation();
	const damageSchema = useMemo(() => buildDamageSchema(t), [t]);
	const { selectedAssetId: assetIdFromContext } = useAppContext();
	const { assetId: rootAssetId } = useParams<{
		assetId: string;
	}>();
	const shouldUpdateDamage = usePermission(ROLE.DAMAGES_UPDATE);

	const [isRecording, setIsRecording] = useState(false);
	const [recordingTime, setRecordingTime] = useState(0);
	const [hasRecording, setHasRecording] = useState(false);
	const [isAssetTreeOpen, setIsAssetTreeOpen] = useState(false);

	const form = useForm<DamageFormValues>({
		resolver: zodResolver(damageSchema),
		disabled: !shouldUpdateDamage,
		defaultValues: {
			assetId: damage?.assetId,
			subAssetId: assetIdFromContext || undefined,
			class: damage?.class ?? '3 - mittelfristige Gefährdung',
			length: damage?.lengthArea?.length ?? 0,
			lengthUnit: damage?.lengthArea?.massureType ? getUnitKeyByLabel(damage?.lengthArea?.massureType) : '1',
			description: damage?.description,

			materialId: damage?.materialId as number,
			damageTypeId: damage?.damageTypeId ?? null,
			statusId: damage?.statusId ?? null,
			categoryId: damage?.categoryId ?? null,
			subCategoryId: damage?.subCategoryId ?? null,
			elevationId: damage?.elevationId ?? null,
			extentId: damage?.extentId ?? null,
			longitudinalId: damage?.longitudinalId ?? null,
			previousDamageStatusId: damage?.previousDamageStatusId ?? null,
			remarkId: damage?.remarkId ?? null,
			transverseId: damage?.transverseId ?? null,
		},
	});
	useRevalidateOnLanguageChange(form);

	// --- DATA FETCHING ---

	const { data: subAssetsResponse } = useQuery(getListSubAssetsOptions(rootAssetId || '', { limit: 300, offset: 0 }));
	const { data: asset } = useQuery(getAssetOptions(rootAssetId || ''));
	const { materialsGroupByCategory: materialGroupBy, materialOptions } = useMaterialOptions();
	const { damageOptions, damageOptionsByMaterialCategoryGroup } = useDamageTypeOptions();

	const subAssetsWithFirstParent = useMemo((): SubAsset[] => {
		if (!subAssetsResponse?.subAssets || !asset?.id) return [];
		const children = getTreeView(subAssetsResponse?.subAssets);
		return [
			{
				id: asset?.id as string,
				name: asset?.name || '',
				parentSubAssetId: null,
				children,
			},
		];
	}, [subAssetsResponse?.subAssets, asset?.id, asset?.name]);

	const getAssetBreadcrumbPath = (assetId: string): string => {
		const path = buildSubAssetPath(subAssetsWithFirstParent, assetId);
		return path.map((s) => s.name || '').join(' > ');
	};

	// --- FORM EFFECTS ---

	const selectedMaterialCombined = form.watch('materialId');
	const selectedType = form.watch('damageTypeId');
	const selectedUnitType = form.watch('lengthUnit');

	const damageTypeOptions = useMemo(() => {
		if (!selectedMaterialCombined || !materialOptions || !damageOptionsByMaterialCategoryGroup) return [];
		const selectedMaterialOption = materialOptions?.find((mat) => mat.id === Number(selectedMaterialCombined));
		if (!selectedMaterialOption) return [];
		return damageOptionsByMaterialCategoryGroup[selectedMaterialOption.category];
	}, [selectedMaterialCombined, materialOptions, damageOptionsByMaterialCategoryGroup]);

	// Calculate current dimension to control visibility
	const currentDimension = useMemo(() => {
		if (!selectedType) return null;
		return damageOptions?.find((opt) => opt.id === Number(selectedType))?.dimension;
	}, [selectedType, damageOptions]);

	// Automatically set unit when Dimension changes based on Type selection
	useEffect(() => {
		// Only auto-update if user has interacted with damageTypeId (isDirty) to prevent overwriting existing value on initial load or when other fields change.
		if (currentDimension !== null && currentDimension !== undefined && form.getFieldState('damageTypeId').isDirty) {
			if (currentDimension === DamageDimension.COUNT) {
				form.setValue('length', 0);
			} else {
				const dimensionUnitMapping: Record<DamageDimension, string> = {
					[DamageDimension.LENGTH]: '1', // 'cm'
					[DamageDimension.AREA]: '2', // 'm²'
					[DamageDimension.VOLUME]: '3', // 'm³'
					[DamageDimension.COUNT]: '1', // 'cm' but will be hidden
				};
				// Map dimension (number) directly to Unit ID (string)
				const targetUnitId = dimensionUnitMapping[currentDimension];

				// Strict check to see if update is needed
				if (form.getValues('lengthUnit') !== targetUnitId) {
					form.setValue('lengthUnit', targetUnitId, {
						shouldValidate: true,
						shouldDirty: true,
						shouldTouch: true,
					});
				}
			}
		}
	}, [currentDimension, form]);

	// Calculate the value ONLY if we are in m2 mode ('2') OR cm mode ('1')
	const calculatedSizeValue = useMemo(() => {
		if (selectedUnitType === '2') {
			// Area
			form.resetField('length');
			return calculateDamageSize(damage?.id ? damage.id : 'new', 'area');
		} else if (selectedUnitType === '1') {
			// Perimeter
			form.resetField('length');
			return calculateDamageSize(damage?.id ? damage.id : 'new', 'perimeter');
		}
		return null;
	}, [damage?.id, calculateDamageSize, selectedUnitType, form.resetField]);

	// 2. Updated Effect to handle Sync and prevent Overwrite for both measurements
	useEffect(() => {
		if (
			(selectedUnitType === '2' || selectedUnitType === '1') &&
			calculatedSizeValue !== null &&
			calculatedSizeValue !== undefined
		) {
			const numValue = parseFloat(String(calculatedSizeValue));

			if (!isNaN(numValue)) {
				const currentValue = form.getValues('length');

				// Check if the user has manually interacted with the field
				const { isDirty } = form.getFieldState('length');

				if (currentValue === 0 || !isDirty) {
					form.setValue('length', numValue, {
						shouldValidate: true,
						shouldDirty: false, // Keep dirty false to allow subsequent auto-updates
					});
				}
			}
		}
	}, [selectedUnitType, calculatedSizeValue, form]);

	useEffect(() => {
		if (!damage && assetIdFromContext) {
			form.setValue('subAssetId', assetIdFromContext);
		}
	}, [assetIdFromContext, form, damage]);

	// Initialize form when editing
	useEffect(() => {
		if (damage) {
			const unitId = getUnitKeyByLabel(damage.lengthArea?.massureType);

			console.log('Initializing form with damage data:', damage);

			form.reset({
				...damage,
				subAssetId: damage.subAssetId || rootAssetId,
				length: damage.lengthArea?.length ?? 0,
				lengthUnit: unitId,
				materialId: damage.materialId as number,
				damageTypeId: damage.damageTypeId ?? null,
				categoryId: damage.categoryId ?? null,
				subCategoryId: damage.subCategoryId ?? null,
				extentId: damage.extentId ?? null,
				longitudinalId: damage.longitudinalId ?? null,
				transverseId: damage.transverseId ?? null,
				elevationId: damage.elevationId ?? null,
				previousDamageStatusId: damage.previousDamageStatusId ?? null,
				remarkId: damage.remarkId ?? null,
				statusId: damage.statusId ?? null,
			});
		}
	}, [damage, form, rootAssetId]);

	useEffect(() => {
		let interval: ReturnType<typeof setInterval>;
		if (isRecording) {
			interval = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
		} else {
			setRecordingTime(0);
		}
		return () => clearInterval(interval);
	}, [isRecording]);

	const toggleRecording = () => {
		if (isRecording) {
			setIsRecording(false);
			setHasRecording(true);
			toast.success(t('damages.toast.voiceNoteRecorded'));
		} else {
			setIsRecording(true);
			setHasRecording(false);
		}
	};

	const deleteRecording = () => {
		setHasRecording(false);
		toast.info(t('damages.toast.recordingDeleted'));
	};

	const preparePayload = (values: DamageFormValues) => {
		// Get the Label (e.g., 'm²') based on ID
		const unitLabel = UNIT_TYPES[values.lengthUnit as unknown as keyof typeof UNIT_TYPES] || 'cm';

		return {
			...values,
			subAssetId: values.subAssetId,
			lengthArea: {
				length: values.length,
				massureType: unitLabel,
			},
			length: values.length,
			lengthUnit: unitLabel,
		};
	};

	// Just Save (Stay on Page)
	const handleSave = (values: DamageFormValues) => {
		const payload = preparePayload(values);
		onSubmit(payload, false);
	};

	// Save and Close (Go Back)
	const handleSaveAndClose = (values: DamageFormValues) => {
		const payload = preparePayload(values);
		onSubmit(payload, true);
	};

	// The page asks for confirmation (with what the delete takes along and the work packages it changes)
	const handleDeleteClick = () => onDelete();

	console.log('Rendering DamageForm with values:', selectedMaterialCombined);

	return (
		<Card className="bg-muted/80 border-border shadow-sm">
			<CardContent className="p-4">
				<Form {...form}>
					<form noValidate data-entity-form onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
						{/* ASSET SELECTOR */}
						<FormField
							control={form.control}
							name="subAssetId"
							render={({ field }) => (
								<FormItem>
									<div>
										<FormLabel className="text-sm font-medium text-foreground mb-2 block">
											{t('damages.form.assetSubAsset')}
										</FormLabel>
										<div className="space-y-2">
											<Button
												type="button"
												variant="outline"
												aria-label={t('damages.form.selectAssetAriaLabel')}
												className="bg-input border border-border rounded p-3 py-5 cursor-pointer hover:border-primary/50 transition-colors hover:bg-accent w-full justify-start"
												onClick={() => setIsAssetTreeOpen(true)}
												disabled={field.disabled}
											>
												<div className="flex items-center justify-between w-full">
													<div className="flex items-center gap-2">
														<Building2 className="h-4 w-4 text-muted-foreground" />
														<span className="text-sm text-foreground truncate">
															{field.value
																? getAssetBreadcrumbPath(field.value)
																: t('damages.form.selectAssetPlaceholder')}
														</span>
													</div>
													<ChevronRight className="h-4 w-4 text-muted-foreground/50" />
												</div>
											</Button>

											<Dialog open={isAssetTreeOpen} onOpenChange={setIsAssetTreeOpen}>
												<DialogContent className="max-w-2xl bg-card border-border">
													<DialogHeader>
														<DialogTitle>{t('damages.form.selectAsset')}</DialogTitle>
													</DialogHeader>
													<div className="py-4">
														<Card className="bg-card border-border overflow-hidden shadow-sm">
															<CardContent className="p-0">
																<div className="p-2 max-h-[60vh] overflow-y-auto">
																	{subAssetsWithFirstParent.length > 0 ? (
																		<SubAssetItem
																			subAsset={subAssetsWithFirstParent[0]}
																			onSelect={(id) => {
																				field.onChange(id);
																				setIsAssetTreeOpen(false);
																			}}
																			selectedId={field.value || ''}
																			viewOnlyMode
																			defaultExpanded
																		/>
																	) : (
																		<div className="p-4 text-center text-sm text-muted-foreground">
																			{t('damages.form.loadingAssets')}
																		</div>
																	)}
																</div>
															</CardContent>
														</Card>
													</div>
												</DialogContent>
											</Dialog>
										</div>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Status & Class */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="statusId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('common.labels.status')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.DAMAGE_STATUSES}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
											firstOptionAsDefault
											placeholder={t('damages.form.selectStatusPlaceholder')}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="class"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.class')}
										</FormLabel>
										<Select onValueChange={field.onChange} defaultValue={field.value} disabled={field.disabled}>
											<FormControl>
												<SelectTrigger className="bg-input h-8 text-sm w-full sm:w-auto">
													<SelectValue placeholder={t('damages.form.selectClassPlaceholder')} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{DAMAGE_CLASSES.map((opt) => (
													<SelectItem key={`damage-class:${opt.value}`} value={opt.value}>
														<div className="flex items-center gap-2">
															<div className={`w-2 h-2 rounded-full ${opt.color}`} />
															{opt.value}
														</div>
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Categories */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="categoryId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.objectCategory')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.DAMAGE_CATEGORIES}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
											placeholder={t('damages.form.selectCategoryPlaceholder')}
											firstOptionAsDefault
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="subCategoryId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.subObjectCategory')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.DAMAGE_SUBCATEGORIES}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
											placeholder={t('damages.form.selectSubCategoryPlaceholder')}
											firstOptionAsDefault
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Material & Type */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="materialId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.material')}
										</FormLabel>
										<Select
											value={field.value ? String(field.value) : ''}
											onValueChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
												form.setValue('damageTypeId', null);
											}}
											disabled={field.disabled}
										>
											<FormControl>
												<SelectTrigger className="bg-input h-8 text-sm w-full sm:w-auto">
													<SelectValue placeholder={t('damages.form.selectMaterialPlaceholder')} />
												</SelectTrigger>
											</FormControl>
											<SelectContent className="max-h-75">
												{Object.entries(materialGroupBy || {}).map(([group, options]) => (
													<SelectGroup key={group}>
														<SelectLabel className="sticky top-0 z-10 bg-muted text-foreground text-xs uppercase tracking-wider pl-2 py-1 font-extrabold shadow-sm border-b border-border">
															{group}
														</SelectLabel>
														{options.map((opt) => (
															<SelectItem key={opt.id} value={String(opt.id)}>
																{opt.label}
															</SelectItem>
														))}
													</SelectGroup>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="damageTypeId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.damageType')}
										</FormLabel>
										<Select
											value={field.value ? String(field.value) : ''}
											onValueChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={!selectedMaterialCombined || damageTypeOptions.length === 0 || field.disabled}
										>
											<FormControl>
												<SelectTrigger className="bg-input disabled:opacity-50 h-8 text-sm w-full sm:w-auto">
													<SelectValue
														placeholder={
															!selectedMaterialCombined
																? t('damages.form.selectMaterialFirst')
																: t('damages.form.selectDamageTypeOption')
														}
													/>
												</SelectTrigger>
											</FormControl>
											<SelectContent className="max-h-75">
												{damageTypeOptions.map((group) => (
													<SelectGroup key={group.name}>
														<SelectLabel className="sticky top-0 z-10 bg-muted text-foreground text-xs uppercase tracking-wider pl-2 py-1 font-extrabold shadow-sm border-b border-border">
															{group.name}
														</SelectLabel>
														{group.options.map((opt) => (
															<SelectItem key={opt.id} value={String(opt.id)}>
																{opt.label}
															</SelectItem>
														))}
													</SelectGroup>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Dimensions */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="grid grid-cols-[1fr_80px_40px] gap-2 items-end">
								<FormField
									control={form.control}
									name="length"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
												{t('damages.form.lengthArea')}
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													{...field}
													value={field.value as string}
													className="bg-input border border-border h-8 text-sm"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								{currentDimension && currentDimension !== DamageDimension.COUNT && (
									<FormField
										control={form.control}
										name="lengthUnit"
										render={({ field }) => (
											<FormItem>
												<Select
													onValueChange={field.onChange}
													// IMPORTANT: Using `key` forces re-render when value changes programmatically
													// This fixes the issue where text remains empty when switching from hidden -> visible
													key={field.value?.toString()}
													value={field.value?.toString()}
													disabled={field.disabled}
												>
													<FormControl>
														<SelectTrigger className="bg-input h-8 text-sm">
															<SelectValue />
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{Object.entries(UNIT_TYPES).map(([key, unitLabel]) => (
															<SelectItem key={key} value={key}>
																{unitLabel}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}
							</div>
							<FormField
								control={form.control}
								name="extentId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.extent')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.DAMAGE_EXTENT}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
											placeholder={t('damages.form.selectExtentPlaceholder')}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Location Grid */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<FormField
								control={form.control}
								name="longitudinalId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.longitudinalPosition')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.LONGITUDINAL_POSITIONS}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="transverseId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.transversePosition')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.TRANSVERSE_POSITIONS}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="elevationId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.verticalPosition')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.ELEVATION_POSITIONS}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Remarks & Existing */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="previousDamageStatusId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.previousDamageStatus')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.PREVIOUS_DAMAGE_STATUSES}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
											placeholder={t('damages.form.selectPreviousStatusPlaceholder')}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="remarkId"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('damages.form.remarksLabel')}
										</FormLabel>
										<ConfigSelect
											dropdown={DROPDOWN_NAMES.DAMAGE_REMARKS}
											value={field.value !== null ? String(field.value) : null}
											onChange={(val) => {
												const num = Number.parseInt(val, 10);
												field.onChange(Number.isNaN(num) ? null : num);
											}}
											disabled={field.disabled}
											placeholder={t('damages.form.selectRemarkPlaceholder')}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Description & Audio */}
						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<div className="flex justify-between items-center">
										<FormLabel className="text-xs font-semibold text-muted-foreground uppercase">
											{t('common.labels.description')}
										</FormLabel>

										<div className="flex items-center gap-2">
											{isRecording && (
												<div className="flex items-center gap-2 text-destructive animate-pulse text-xs font-medium mr-2">
													<div className="w-2 h-2 bg-destructive rounded-full" />
													{t('damages.form.recording', {
														minutes: Math.floor(recordingTime / 60),
														seconds: (recordingTime % 60).toString().padStart(2, '0'),
													})}
												</div>
											)}

											{hasRecording && !isRecording && (
												<div className="flex items-center gap-2 bg-primary/10 text-primary px-2 py-1 rounded-md text-xs border border-primary/20 mr-2">
													<Play className="h-3 w-3" />
													<span>{t('damages.form.voiceNote')}</span>
													<button type="button" onClick={deleteRecording} className="ml-1 hover:text-primary/80">
														<X className="h-3 w-3" />
													</button>
												</div>
											)}

											<Button
												type="button"
												size="sm"
												variant={isRecording ? 'destructive' : 'outline'}
												className={cn('h-7 px-2 text-xs gap-1.5', !isRecording && 'bg-muted/50 hover:bg-muted')}
												onClick={toggleRecording}
											>
												{isRecording ? <Square className="h-3 w-3 fill-current" /> : <Mic className="h-3 w-3" />}
												{isRecording ? t('damages.form.stopRecording') : t('damages.form.recordAudio')}
											</Button>
										</div>
									</div>
									<FormControl>
										<Textarea
											{...field}
											value={field.value ?? ''}
											className="bg-input border border-border min-h-[100px]"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Footer Buttons */}
						<div className="flex items-center justify-between gap-2 pt-4">
							<div>
								{restoreButton}
								<Restricted to={ROLE.DAMAGES_DELETE}>
									{damage?.id && (
										<DeleteButton onClick={handleDeleteClick} pending={isSubmitting} className="shadow-sm" />
									)}
								</Restricted>
							</div>
							<div className="flex gap-1.5">
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={onCancel}
									className="bg-muted/50 border-border text-foreground hover:bg-muted"
								>
									{t('common.actions.cancel')}
								</Button>
								<Restricted to={ROLE.DAMAGES_UPDATE}>
									<Button
										type="button"
										size="sm"
										disabled={isSubmitting}
										onClick={form.handleSubmit(handleSave)}
										className="shadow-sm"
									>
										{isSubmitting && <Spinner />}
										<Save className="h-4 w-4" />
										{t('common.actions.save')}
									</Button>
									<Button
										type="button"
										size="sm"
										disabled={isSubmitting}
										onClick={form.handleSubmit(handleSaveAndClose)}
										className="shadow-sm hidden sm:inline-flex"
									>
										{isSubmitting && <Spinner />}
										<Save className="h-4 w-4" />
										{t('damages.form.saveAndClose')}
									</Button>
								</Restricted>
							</div>
						</div>
					</form>
				</Form>
			</CardContent>
		</Card>
	);
}
